import React, { FunctionComponent, useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import { AddContactIcon, Button, CheckboxWithLabel, EmailCard, LoadingLine, Theme } from '@spoiler-alert/ui-library';
import { ModifyCuratedBSR, ToggleBuyerInCuratedList } from '../../graphql/mutations';
import EditContactModal from './edit-contact-modal';
import { BSR, BuyerSelectionProps, ContactType, CuratedListMutationResult } from './curated-list-types';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    '& h4': {
      marginTop: 0,
    },
    '& h5': {
      margin: 0,
      fontSize: 16,
    },
    '& h6': {
      margin: 0,
      marginBottom: 10,
      fontSize: 14,
      borderBottom: `2px solid ${Theme.greyDark}`,
      '& span': {
        fontSize: 12,
        color: Theme.green80,

        '&.award': {
          color: Theme.pink,
        },
      },
    },
  },
  buyer: {
    background: Theme.grey5,
    margin: 0,
    padding: [8, 15],
    boxShadow: '0 0 5px rgba(0,0,0,0.3)',
    borderRadius: 10,
  },
  buyerDisabled: {
    extend: 'buyer',
    opacity: 0.5,
    boxShadow: 'none',
    background: 'none',
    '& $row': {
      pointerEvents: 'none',
    },
  },
  row: {
    display: 'flex',
    width: '100%',
    gap: 10,
    '&>div': {
      flexBasis: '50%',
    },
  },
  bsrType: {
    paddingBottom: 15,
  },
  contactType: {},
  emailList: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    '&>div>div': {
      padding: [8, 8],
      height: 'auto',
      margin: 0,
      '&>span': {
        fontSize: '.8rem',
      },
      '&>div': {
        height: 30,
        '&>div': {
          paddingTop: 6,
        },
      },
    },
  },
});

const BuyerSelection: React.FC<BuyerSelectionProps> = ({
  sellerSiteId,
  buyerSiteId,
  defaultBSRs,
  curatedBSRs,
  curatedListId,
  handleError,
}) => {
  const classes = useStyles();
  const [editingContact, setEditingContact] = useState<{
    bsr: BSR;
    contactType: ContactType;
    email: string;
  } | null>(null);

  const [toggleBuyer, { loading: loadingToggleBuyer }] = useMutation<
    { toggleBuyerInCuratedList: CuratedListMutationResult },
    {
      curatedListId: string;
      sellerSiteId: string;
      buyerSiteIds: string[];
      enable: boolean;
    }
  >(ToggleBuyerInCuratedList);
  const [saveEmails, { loading: loadingSaveEmails }] = useMutation<
    { modifyCuratedBSR: CuratedListMutationResult },
    {
      curatedListId: string;
      buyerSellerRelationshipId: string;
      buyerUserEmails: string[];
      sellerUserEmails: string[];
    }
  >(ModifyCuratedBSR);

  const loading = loadingSaveEmails || loadingToggleBuyer;

  const bsrsToUse = curatedBSRs || defaultBSRs;
  const awardBSR = bsrsToUse.find((bsr) => bsr.type === 'AWARD');
  const offerBSR = bsrsToUse.find((bsr) => bsr.type === 'OFFER');
  const sellerEmailsForOffers = offerBSR?.sellerUserEmails || [];
  const buyerEmailsForOffers = offerBSR?.buyerUserEmails || [];
  const sellerEmailsForAwards = awardBSR?.sellerUserEmails || [];
  const buyerEmailsForAwards = awardBSR?.buyerUserEmails || [];

  const checked = curatedBSRs && curatedBSRs.length > 0;

  const cleanBSRForEditing = (bsr: BSR) => ({
    id: bsr.sellerSiteId === sellerSiteId ? bsr.id : '',
    sellerSiteId,
    buyerSiteId,
    type: bsr.type,
    sellerUserEmails: [...bsr.sellerUserEmails],
    buyerUserEmails: [...bsr.buyerUserEmails],
  });

  const addEmail = (existingBSR: BSR, type: ContactType) => {
    setEditingContact({
      bsr: cleanBSRForEditing(existingBSR),
      contactType: type,
      email: '',
    });
  };

  const startEditing = (bsr: BSR, contactType: ContactType, email: string) => {
    setEditingContact({ bsr: cleanBSRForEditing(bsr), contactType, email });
  };

  const updateEmails = async (bsr: BSR) => {
    try {
      const { id: buyerSellerRelationshipId, sellerUserEmails, buyerUserEmails } = bsr;
      const result = await saveEmails({
        variables: {
          curatedListId,
          buyerSellerRelationshipId,
          buyerUserEmails,
          sellerUserEmails,
        },
      });
      if (!result.data?.modifyCuratedBSR) throw new Error('Something went wrong');
      const { errors } = result.data?.modifyCuratedBSR;
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      handleError((error as Error).message);
    }
  };

  const handleToggleBuyer = async (enable: boolean) => {
    try {
      const result = await toggleBuyer({
        variables: {
          curatedListId,
          sellerSiteId,
          buyerSiteIds: [buyerSiteId],
          enable,
        },
      });
      if (!result.data?.toggleBuyerInCuratedList) throw new Error('Something went wrong');
      const { errors } = result.data?.toggleBuyerInCuratedList;
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      handleError((error as Error).message);
    }
  };

  const handleCheckbox = useCallback(
    ({ checked: enable }) => {
      handleToggleBuyer(enable);
    },
    [buyerSiteId],
  );

  const handleEdit = (modifiedEmail: string, contactType: ContactType, oldEmail: string) => {
    if (!editingContact) return;
    const { bsr } = editingContact;
    let list = [];
    if (contactType === 'BUYER') {
      list = bsr.buyerUserEmails;
    } else {
      list = bsr.sellerUserEmails;
    }
    if (oldEmail === '') {
      list.push(modifiedEmail);
    } else {
      const index = list.indexOf(oldEmail);
      list[index] = modifiedEmail;
    }
    updateEmails(bsr);
    setEditingContact(null);
  };

  const handleDeleteContact = async () => {
    if (!editingContact) return;
    const { email, contactType, bsr } = editingContact;
    let list = [];
    if (contactType === 'BUYER') {
      list = bsr.buyerUserEmails;
    } else {
      list = bsr.sellerUserEmails;
    }
    const index = list.indexOf(email);
    list.splice(index, 1);
    updateEmails(bsr);
    setEditingContact(null);
  };

  const TypedAddContactIcon = AddContactIcon as FunctionComponent<{ style?: object }>;

  return (
    <div className={`${classes.wrap} ${checked ? classes.buyer : classes.buyerDisabled}`}>
      <LoadingLine loading={loading} />
      <h4>
        <CheckboxWithLabel
          id={`checkbox-${buyerSiteId}`}
          key={buyerSiteId}
          checked={checked}
          label={defaultBSRs[0].buyerSiteName}
          onChecked={handleCheckbox}
          value={buyerSiteId}
          disabled={false}
          testId="buyer-check"
        />
      </h4>
      <div className={classes.row}>
        <div className={classes.bsrType}>
          <div className={classes.row}>
            <div className={classes.contactType}>
              <h6>
                Buyer <span>OFFER</span>
              </h6>
              {offerBSR && (
                <div className={classes.emailList}>
                  {buyerEmailsForOffers.map((email) => (
                    <EmailCard
                      key={email}
                      email={email}
                      backgroundColor={Theme.green}
                      borderColor={Theme.tealDark}
                      handleEdit={() => startEditing(offerBSR, 'BUYER', email)}
                    />
                  ))}
                  <Button
                    disabled={!checked}
                    icon={TypedAddContactIcon}
                    secondary
                    onClick={() => addEmail(offerBSR, 'BUYER')}
                  />
                </div>
              )}
            </div>
            <div className={classes.contactType}>
              <h6>
                Seller <span>OFFER</span>
              </h6>
              {offerBSR && (
                <div className={classes.emailList}>
                  {sellerEmailsForOffers.map((email) => (
                    <EmailCard
                      key={email}
                      email={email}
                      backgroundColor={Theme.green}
                      borderColor={Theme.greenDark}
                      handleEdit={() => startEditing(offerBSR, 'SELLER', email)}
                    />
                  ))}
                  <Button
                    disabled={!checked}
                    icon={TypedAddContactIcon}
                    secondary
                    onClick={() => addEmail(offerBSR, 'SELLER')}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={classes.bsrType}>
          <div className={classes.row}>
            <div className={classes.contactType}>
              <h6>
                Buyer <span className="award">AWARD</span>
              </h6>
              {awardBSR && (
                <div className={classes.emailList}>
                  {buyerEmailsForAwards.map((email) => (
                    <EmailCard
                      key={email}
                      email={email}
                      backgroundColor={Theme.orange5}
                      borderColor={Theme.pink}
                      handleEdit={() => startEditing(awardBSR, 'BUYER', email)}
                    />
                  ))}
                  <Button
                    disabled={!checked}
                    icon={TypedAddContactIcon}
                    secondary
                    onClick={() => addEmail(awardBSR, 'BUYER')}
                  />
                </div>
              )}
            </div>
            <div className={classes.contactType}>
              <h6>
                Seller <span className="award">AWARD</span>
              </h6>
              {awardBSR && (
                <div className={classes.emailList}>
                  {sellerEmailsForAwards.map((email) => (
                    <EmailCard
                      key={email}
                      email={email}
                      backgroundColor={Theme.red5}
                      borderColor={Theme.pink}
                      handleEdit={() => startEditing(awardBSR, 'SELLER', email)}
                    />
                  ))}
                  <Button
                    disabled={!checked}
                    icon={TypedAddContactIcon}
                    secondary
                    onClick={() => addEmail(awardBSR, 'SELLER')}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <EditContactModal
        open={!!editingContact}
        onHide={() => setEditingContact(null)}
        handleSubmit={handleEdit}
        loading={false}
        email={editingContact?.email}
        selectedContactType={editingContact?.contactType}
        deleteContact={handleDeleteContact}
      />
    </div>
  );
};

export default BuyerSelection;
